<template>
  <div v-for="(module, index) in contentModules" v-if="contentModules.length">
    <IntersectWrapper
      v-if="module.component === ContentModules.COVER"
      @mounted="trackModuleExists(contentModules, module)"
      @intersects="trackModuleViewed(contentModules, module)"
    >
      <CRCover
        v-append-parameters="urlParams"
        :module="module"
        :lazy="index > 2"
        :job-count-string="jobsCountAll"
        @emitted-click="trackModuleClick(contentModules, module)"
      />
    </IntersectWrapper>

    <IntersectWrapper
      v-else-if="module.component === ContentModules.CATEGORIES"
      @mounted="trackModuleExists(contentModules, module)"
      @intersects="trackModuleViewed(contentModules, module)"
    >
      <CRCategories
        v-append-parameters="urlParams"
        :module="module"
        :lazy="index > 2"
        :job-counts="jobsCountCategories"
        :zero-jobs-text="zeroJobsText"
        @emitted-click="
          (e) => {
            trackModuleClick(contentModules, module, e.child);
          }
        "
      />
    </IntersectWrapper>

    <IntersectWrapper
      v-else-if="module.component === ContentModules.CARDS"
      @mounted="trackModuleExists(contentModules, module)"
      @intersects="trackModuleViewed(contentModules, module)"
    >
      <CRCards
        v-append-parameters="urlParams"
        :module="module"
        :lazy="index > 2"
        @emitted-click="
          (e) => {
            trackModuleClick(contentModules, module, e.child);
          }
        "
      />
    </IntersectWrapper>

    <IntersectWrapper
      v-else-if="module.component === ContentModules.BENEFITS"
      @mounted="trackModuleExists(contentModules, module)"
      @intersects="trackModuleViewed(contentModules, module)"
    >
      <CRBenefits
        v-append-parameters="urlParams"
        :module="module"
        :lazy="index > 2"
        @emitted-click="trackModuleClick(contentModules, module)"
      />
    </IntersectWrapper>

    <IntersectWrapper
      v-else-if="module.component === ContentModules.IMAGETEXT"
      @mounted="trackModuleExists(contentModules, module)"
      @intersects="trackModuleViewed(contentModules, module)"
    >
      <CRImageText
        v-append-parameters="urlParams"
        :module="module"
        :lazy="index > 2"
        @emitted-click="trackModuleClick(contentModules, module)"
      />
    </IntersectWrapper>
    <IntersectWrapper
      v-else-if="module.component === ContentModules.TEXT"
      @mounted="trackModuleExists(contentModules, module)"
      @intersects="trackModuleViewed(contentModules, module)"
    >
      <CRText
        v-append-parameters="urlParams"
        :module="module"
        :lazy="index > 2"
        @emitted-click="trackModuleClick(contentModules, module)"
      />
    </IntersectWrapper>

    <IntersectWrapper
      v-else-if="module.component === ContentModules.CHAPTER"
      @mounted="trackModuleExists(contentModules, module)"
      @intersects="trackModuleViewed(contentModules, module)"
    >
      <CRChapter
        v-append-parameters="urlParams"
        :module="module"
        :lazy="index > 2"
        @emitted-click="trackModuleClick(contentModules, module)"
      />
    </IntersectWrapper>

    <IntersectWrapper
      v-else-if="module.component === ContentModules.VIDEO"
      @mounted="trackModuleExists(contentModules, module)"
      @intersects="trackModuleViewed(contentModules, module)"
    >
      <CRVideo
        v-append-parameters="urlParams"
        :module="module"
        :lazy="index > 2"
        @emitted-click="trackModuleClick(contentModules, module)"
      />
    </IntersectWrapper>

    <IntersectWrapper
      v-if="module.component === ContentModules.GALLERY"
      @mounted="trackModuleExists(contentModules, module)"
      @intersects="trackModuleViewed(contentModules, module)"
    >
      <CRGallery
        v-append-parameters="urlParams"
        :module="module"
        :lazy="index > 2"
        @emitted-click="
          (e) => {
            trackModuleClick(contentModules, module, e.child);
          }
        "
      />
    </IntersectWrapper>

    <IntersectWrapper
      v-else-if="module.component === ContentModules.FAQ"
      @mounted="trackModuleExists(contentModules, module)"
      @intersects="trackModuleViewed(contentModules, module)"
    >
      <CRFaqs
        v-append-parameters="urlParams"
        :module="module"
        :lazy="index > 2"
      />
    </IntersectWrapper>

    <IntersectWrapper
      v-else-if="module.component === ContentModules.HIRINGPROCESS"
      @mounted="trackModuleExists(contentModules, module)"
      @intersects="trackModuleViewed(contentModules, module)"
    >
      <CRHiringProcess
        v-append-parameters="urlParams"
        :module="module"
        :lazy="index > 2"
      />
    </IntersectWrapper>

    <IntersectWrapper
      v-else-if="module.component === ContentModules.SEARCH_AND_FILTER"
      @mounted="trackModuleExists(contentModules, module)"
      @intersects="trackModuleViewed(contentModules, module)"
    >
      <SearchAndFilterModuleContainer
        :module="module"
      />
    </IntersectWrapper>

    <IntersectWrapper
      v-else-if="module.component === ContentModules.SLIDER"
      @mounted="trackModuleExists(contentModules, module)"
      @intersects="trackModuleViewed(contentModules, module)"
    >
      <CRSliderModule
        v-append-parameters="urlParams"
        :module="module"
        :lazy="index > 2"
        @emitted-click="
          (e) => {
            trackModuleClick(contentModules, module, e.child);
          }
        "
        @slide-visible="
          (e) => {
            trackModuleViewed(contentModules, module, e.child);
          }
        "
      />
    </IntersectWrapper>

    <IntersectWrapper
      v-else-if="module.component === ContentModules.HERO"
      @mounted="trackModuleExists(contentModules, module)"
      @intersects="trackModuleViewed(contentModules, module)"
    >
      <CRHero
        v-append-parameters="urlParams"
        :module="getModuleWithPitchYouButton(module)"
        :job-data="jobData"
        @emitted-click="onHeaderCtaClick"
      />
    </IntersectWrapper>

    <transition v-if="module.component === ContentModules.HERO && shouldShowActionBar" name="slide-up">
      <CRActionBar
        v-show="showActionBar"
        :module="getModuleWithPitchYouButton(module)"
        :action-bar-config="props.actionBarConfig"
        :job-data="jobData"
        :navigation-global="jobShop?.navigation"
        class="cr-action-bar"
        @emitted-click="onHeaderCtaClick"
      />
    </transition>

    <IntersectWrapper
      v-else-if="module.component === ContentModules.JOBTEXTFIELD"
      @mounted="trackModuleExists(contentModules, module)"
      @intersects="trackModuleViewed(contentModules, module)"
    >
      <CRJobTextField
        :module="module"
        :job-data="jobData"
      />
    </IntersectWrapper>

    <IntersectWrapper
      v-else-if="module.component === ContentModules.FORM && showApplicationForm"
      @mounted="trackModuleExists(contentModules, module)"
      @intersects="onFormIntersect(contentModules, module)"
    >
      <CRFormRendererContainer :module="module" :job="jobData" />
    </IntersectWrapper>
  </div>
</template>

<script setup lang="ts">
import { ContentModules } from "@/utils/enums/ContentModules"
import { ApplicationType } from "@/utils/enums/ApplicationType"
import { useTypesenseJobCounts } from "@/composables/useTypesenseJobCounts"
import { useActionBarScroll } from "@/composables/useActionBarScroll"
import CRFormRendererContainer from "@/components/CRFormRendererContainer.vue"

const props = defineProps<{
  contentModules: ContentModule[]
  jobData?: FlatJob
  actionBarConfig?: any
}>()

const urlParams = useQueryHeaders(useRequestHeaders())
const { t } = useI18n()
const { data: jobShop } = useNuxtData("jobShopData")
const jobShopLocale = ref(jobShop.value?.locale)

const zeroJobsText = computed(() => t("module.jobs-text", 0, { locale: jobShopLocale.value }))

const { jobsCountCategories, jobsCountAll } = await useTypesenseJobCounts()

const { showActionBar, shouldShowActionBar, getModuleWithPitchYouButton } = useActionBarScroll({
  contentModules: props.contentModules,
  actionBarConfig: props.actionBarConfig,
  jobData: props.jobData,
})

const { onHeaderCtaClick, onFormIntersect } = useHeaderActionTracking(
  computed(() => props.jobData),
)

const showApplicationForm = computed(() => {
  const applicationType = props.jobData?.application_type

  if (!applicationType) {
    return false
  }

  return applicationType !== ApplicationType.EXTERNAL && applicationType !== ApplicationType.NONE
})
</script>

<style lang="scss" scoped>
.cr-action-bar {
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  z-index: 90;
}

.slide-up-enter-active,
.slide-up-leave-active {
  transition: transform 0.3s ease, opacity 0.3s ease;
}

.slide-up-enter-from,
.slide-up-leave-to {
  transform: translateY(100%);
  opacity: 0;
}
</style>
